/** PLANȘA ARHITECTULUI sub geometria editorului: desenul original al foii
 *  (DWG, DXF sau PDF vectorial), nu interpretarea lui din document.
 *
 *  Documentul canonic poartă pe fiecare planșeu configurația importului
 *  (`props["structure.import"]`, scrisă de `canonic._props_import`): fișierul
 *  din care a venit nivelul (`fisier_id`), dreptunghiul de pe foaie din care
 *  s-a luat planul și deplasarea cu care s-a aliniat peste nivelul de jos
 *  (`incadrare`: `bbox_mm`, `dx_mm`, `dy_mm`) și unitatea foii
 *  (`unitate_factor_mm`). Desenul foii vine de la platformă
 *  (`apiStructura.fileDrawingUrl`), în unitățile ei; aici se decupează și se
 *  mută exact cum a făcut importul (`arhimport.arhitectura.desen_nivel`:
 *  `planse.decupeaza` + `planse.muta_desenul`), iar cadrul comun al importului
 *  e cel al milimetrilor georeferențiați din care editorul își desenează
 *  pereții (`canonic.arh_din_document`, `editare._poligon`): o singură matrice
 *  SVG (`matriceaPlansei`) duce foaia pe ecran, peste pereții pe care editorul
 *  îi are deja.
 *
 *  Nivelul fără planșă (arhitectura GENERATĂ) n-are `fisier_id`:
 *  `importulPlansei` întoarce `null`, iar editorul desenează planul
 *  documentului, în convenția tehnică a ecranului de arhitectură. La fel
 *  când foaia a fost ștearsă din dosar după import (`plansaStearsa`). */
import type { PlansaImport, PlanseImport } from "../nav/api";
import { northingFirst, type CanonicalDoc } from "../viewer/canonical";
import type { Crs } from "./calcArhitectura";
import { displayColor, displayFill } from "./culoriDesen";
import { laEcran, type Cadru, type Pt } from "./editorGeometrie";
import type { DrawingData, DrawingEntity, DrawingPoly, DrawingText } from "./tipuri";

/** Marginea decupării în jurul planului, în mm — aceeași cu
 *  `arhimport.planse.GRUP_GAP_MM`: cotele și trimiterile de lângă plan rămân,
 *  desenul vecin de pe foaie nu. */
export const MARGINE_DECUPARE_MM = 15_000;
/** Peste atâtea segmente de hașură pe o foaie, hașurile se desenează ca
 *  tentă, nu linie cu linie: un plan cu zidăria hașurată are sute de mii. */
export const SEGMENTE_HASURA_MAX = 40_000;

export type ImportulPlansei = {
  fisierId: string;
  /** dreptunghiul planului pe foaie, în mm (`bbox_mm`); fără el, toată foaia */
  bbox: [number, number, number, number] | null;
  /** deplasarea de aliniere peste nivelul de jos, în mm */
  dx: number;
  dy: number;
  /** câți milimetri are o unitate a foii (1, 10 sau 1000) */
  factor: number;
};

/** Configurația importului de pe planșeul `storey` al documentului; `null`
 *  pentru un nivel fără planșă. */
export function importulPlansei(doc: CanonicalDoc,
                                storey: string | null | undefined): ImportulPlansei | null {
  if (!storey) return null;
  const o = doc.objects.find((x) => x.id === storey && x.type === "Storey");
  const imp = (o?.props as Record<string, any> | undefined)?.["structure.import"];
  const fid = imp?.fisier_id;
  if (fid == null || fid === "") return null;
  const inc = imp.incadrare ?? {};
  const bb = Array.isArray(inc.bbox_mm) && inc.bbox_mm.length === 4
    ? (inc.bbox_mm.map(Number) as [number, number, number, number]) : null;
  return { fisierId: String(fid), bbox: bb,
           dx: Number(inc.dx_mm) || 0, dy: Number(inc.dy_mm) || 0,
           factor: Number(imp.unitate_factor_mm) || 1 };
}

/** Foaia nivelului, dacă registrul o știe ștearsă din dosar după import
 *  (`SolutieRand.planse.lipsa` al arhitecturii); `null` altfel — și când
 *  registrul n-a spus nimic, ca până acum. Documentul arhitecturii e imutabil
 *  și își poartă `fisier_id` mai departe, deci fără regula asta foaia s-ar
 *  cere la fiecare deschidere și ar întoarce 404. */
export function plansaStearsa(importul: ImportulPlansei | null,
                              planse: PlanseImport | null | undefined): PlansaImport | null {
  if (!importul || !planse) return null;
  return planse.lipsa.find((p) => p.fisier_id === importul.fisierId) ?? null;
}

/** Entitățile foii din jurul planului — TOATE punctele lor în dreptunghi (plus
 *  margine), ca `planse.decupeaza`: o cotă care pleacă 100 m mai încolo ar
 *  trage după ea desenul vecin. În unitățile foii; fără dreptunghi, toate. */
export function decupeaza(entitati: readonly DrawingEntity[], imp: ImportulPlansei,
                          margine = MARGINE_DECUPARE_MM): DrawingEntity[] {
  if (!imp.bbox) return [...entitati];
  const f = imp.factor;
  const x0 = imp.bbox[0] - margine, y0 = imp.bbox[1] - margine;
  const x1 = imp.bbox[2] + margine, y1 = imp.bbox[3] + margine;
  const inauntru = (x: number, y: number) =>
    x * f >= x0 && x * f <= x1 && y * f >= y0 && y * f <= y1;
  return entitati.filter((e) => {
    if (e.t !== "p") return inauntru(e.x, e.y);
    for (const p of e.pts) if (!inauntru(p[0], p[1])) return false;
    for (const h of e.holes ?? []) for (const p of h) if (!inauntru(p[0], p[1])) return false;
    for (const s of e.segs ?? []) if (!inauntru(s[0], s[1]) || !inauntru(s[2], s[3])) return false;
    return e.pts.length > 0 || (e.holes?.length ?? 0) > 0 || (e.segs?.length ?? 0) > 0;
  });
}

/** Punctul foii (unitățile ei) → milimetri georeferențiați, cadrul din care
 *  editorul își desenează pereții: adus la mm și mutat în cadrul comun al
 *  importului, apoi citit ca documentul (`arh_din_document._mm`: punctul față
 *  de origine, perechea întoarsă după CRS, originea adunată). */
export function spreGeoreferentiat(imp: ImportulPlansei, crs: Crs,
                                   p: readonly [number, number]): Pt {
  const x = p[0] * imp.factor + imp.dx;
  const y = p[1] * imp.factor + imp.dy;
  if (!northingFirst(crs as { epsg?: number } | null)) return [x, y];
  const ox = Number(crs?.origin?.[0] ?? 0) * 1000;
  const oy = Number(crs?.origin?.[1] ?? 0) * 1000;
  return [y - oy + ox, -(x - ox) + oy];
}

/** Matricea SVG (`matrix(a b c d e f)`) care duce foaia pe ecranul
 *  editorului: drumul de mai sus, rotit cu −θ în cadrul clădirii
 *  (`editare._poligon`) și adus la ecran (`laEcran`) — afin, scos din imaginea
 *  a trei puncte, ca `calcArhitectura.transformarea`. */
export function matriceaPlansei(imp: ImportulPlansei, crs: Crs, thetaGrade: number,
                                cadru: Cadru): string {
  const th = -(thetaGrade * Math.PI) / 180;
  const c = Math.cos(th);
  const s = Math.sin(th);
  const f = (p: readonly [number, number]): Pt => {
    const [x, y] = spreGeoreferentiat(imp, crs, p);
    return laEcran(cadru, [x * c - y * s, x * s + y * c]);
  };
  const o = f([0, 0]);
  const ex = f([1, 0]);
  const ey = f([0, 1]);
  return `matrix(${ex[0] - o[0]} ${ex[1] - o[1]} ${ey[0] - o[0]} `
       + `${ey[1] - o[1]} ${o[0]} ${o[1]})`;
}

/** Desenul foii strâns în câteva trasee SVG: umplerile (sub tot), hașurile,
 *  liniile pe culoare și grosime, și textele — în unitățile foii. */
export type Trasee = {
  umpleri: { culoare: string; d: string }[];
  hasuri: { culoare: string; d: string }[];
  linii: { culoare: string; latime: number; d: string }[];
  texte: DrawingText[];
};

/** Grosimea liniei pe ecran, în pixeli, ca în vizorul planșelor. */
export function latimeaLiniei(e: DrawingPoly): number {
  if (e.w) return Math.min(4, Math.max(1, e.w * 0.05));
  return e.et === "OUTLINE" || e.et === "FOUNDATION" ? 2 : 1;
}

/** Traseele entităților `entitati` ale desenului `desen` (layerele stinse
 *  sar), cu coordonatele rotunjite la 0,1 mm după unitatea foii. */
export function traseele(desen: DrawingData, entitati: readonly DrawingEntity[],
                         factor: number): Trasee {
  const prec = factor >= 1000 ? 1e4 : factor >= 10 ? 1e3 : 100;
  const nr = (v: number) => String(Math.round(v * prec) / prec);
  const inel = (pts: readonly (readonly [number, number])[], inchis: boolean) => {
    if (pts.length < 2) return "";
    let d = `M${nr(pts[0][0])} ${nr(pts[0][1])}`;
    for (let i = 1; i < pts.length; i++) d += `L${nr(pts[i][0])} ${nr(pts[i][1])}`;
    return inchis ? d + "Z" : d;
  };
  const inele = (e: DrawingPoly) =>
    [e.pts, ...(e.holes ?? [])].map((r) => inel(r, !!e.closed)).join("");
  const vede = (e: DrawingEntity) => desen.layers[e.l]?.on !== false;
  const umpleri = new Map<string, string>();
  const hasuri = new Map<string, string>();
  const linii = new Map<string, { culoare: string; latime: number; d: string }>();
  const texte: DrawingText[] = [];
  const adauga = (m: Map<string, string>, cheie: string, d: string) =>
    m.set(cheie, (m.get(cheie) ?? "") + d);
  let segmente = 0;
  for (const e of entitati) if (e.t === "p" && e.segs) segmente += e.segs.length;
  const tenta = segmente > SEGMENTE_HASURA_MAX;
  for (const e of entitati) {
    if (!vede(e)) continue;
    if (e.t === "t") { texte.push(e); continue; }
    if (e.t !== "p") continue;
    if (e.fill) adauga(umpleri, e.et === "HATCH" ? displayFill(e.fill) : displayColor(e.fill), inele(e));
    const culoare = displayColor(e.c);
    if (e.segs) {
      if (tenta) adauga(umpleri, culoare + "26", inele({ ...e, closed: 1 }));
      else {
        let d = "";
        for (const s of e.segs) d += `M${nr(s[0])} ${nr(s[1])}L${nr(s[2])} ${nr(s[3])}`;
        adauga(hasuri, culoare, d);
      }
      continue;                        // hașura n-are contur al ei
    }
    if (e.et === "HATCH") continue;    // umplerea plină e deja pusă
    const latime = latimeaLiniei(e);
    const cheie = `${culoare}|${latime}`;
    const l = linii.get(cheie) ?? { culoare, latime, d: "" };
    l.d += inele(e);
    linii.set(cheie, l);
  }
  return {
    umpleri: [...umpleri].map(([culoare, d]) => ({ culoare, d })),
    hasuri: [...hasuri].map(([culoare, d]) => ({ culoare, d })),
    linii: [...linii.values()],
    texte,
  };
}
